<template>
  <div class="pagination__container">
    <div class="pagination__total-records">{{ totalRows }} records</div>
    <div class="pagination__paging-controls">
      <div class="action" :disabled="!hasPrevious() || disableControls" @click="first()">
        <n-icon icon="skip-previous" />
      </div>
      &nbsp;
      <div class="action" :disabled="!hasPrevious() || disableControls" @click="previous()">
        <n-icon icon="chevron-left" />
      </div>
      <div class="pagination__paging-controls--current">Page {{ currentPage }} of {{ numberOfPages() }}</div>
      <div class="action" :disabled="!hasNext() || disableControls" @click="next()">
        <n-icon icon="chevron-right" />
      </div>
      &nbsp;
      <div class="action" :disabled="!hasNext() || disableControls" @click="last()">
        <n-icon icon="skip-next" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import NIcon from '../../brand/icon/icon.vue';

export interface Page {
  start: number;
  end: number;
}

defineOptions({
  name: 'n-pagination',
});

const emit = defineEmits<{
  (e: 'page-changed', page: Page): void;
}>();

const props = defineProps({
  itemsTotal: {
    type: Number,
    default: 0,
  },
  itemsPerPage: {
    type: Number,
    default: 50,
  },
  page: {
    type: Number,
    default: 1,
  },
  disableControls: {
    type: Boolean,
    default: false,
  },
});

const currentPage = ref(1);
const totalRows = ref(0);

onMounted(() => {
  totalRows.value = props.itemsTotal || 0;
  currentPage.value = props.page || 1;
});

watch(
  () => props.itemsTotal,
  (current: number, previous: number) => {
    totalRows.value = current;

    if (totalRows.value < 0) {
      totalRows.value = 0;
    }

    // if new total means that our current page is invalid, then reset back to the first page
    if (currentPage.value > 1 && isPageOutOfBounds(currentPage.value)) {
      currentPage.value = 1;
      pageChanged();
    }
  },
);

function numberOfPages() {
  if (totalRows.value <= 0) {
    return 1;
  }

  return Math.max(Math.ceil(totalRows.value / props.itemsPerPage), 1);
}

function last() {
  if (props.disableControls) {
    return;
  }

  if (hasNext()) {
    currentPage.value = numberOfPages();
    pageChanged();
  }
}

function first() {
  if (props.disableControls) {
    return;
  }

  if (hasPrevious()) {
    currentPage.value = 1;
    pageChanged();
  }
}

function next() {
  if (props.disableControls) {
    return;
  }

  if (hasNext()) {
    currentPage.value += 1;
    pageChanged();
  }
}

function previous() {
  if (props.disableControls) {
    return;
  }

  if (hasPrevious()) {
    currentPage.value -= 1;
    pageChanged();
  }
}

function hasNext() {
  const next = currentPage.value + 1;
  return !isPageOutOfBounds(next);
}

function hasPrevious() {
  return currentPage.value > 1;
}

function startOfPage(pageNumber?: number): number {
  // default to current page if page number not supplied
  const pageNum = typeof pageNumber !== 'undefined' ? pageNumber : currentPage.value;
  return (pageNum - 1) * props.itemsPerPage + 1;
}

function endOfPage(pageNumber?: number): number {
  // default to current page if page number not supplied
  const pageNum = typeof pageNumber !== 'undefined' ? pageNumber : currentPage.value;
  return Math.min(pageNum * props.itemsPerPage, totalRows.value);
}

function pageChanged() {
  // our rows are 1-based, but consumers of this will likely have their records stored in 0-based arrays
  emit('page-changed', {
    start: startOfPage() - 1,
    end: endOfPage(),
  });
}

function isPageOutOfBounds(pageNumber: number): boolean {
  return startOfPage(pageNumber) > totalRows.value;
}
</script>
<style scoped lang="scss">
.pagination__container {
  flex: 1;
  display: flex;
  padding: 0.5rem;
  line-height: 1;
}

.pagination__total-records {
  font-size: 0.875rem;
  opacity: 0.75;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.pagination__paging-controls {
  display: flex;
  flex: 1;
  justify-content: flex-end;
  align-items: center;

  > .action {
    stroke: #000;
    cursor: pointer;
    user-select: none;

    &[disabled] {
      opacity: 0.2;
      cursor: default;
    }
  }
}

.pagination__paging-controls--current {
  font-size: 0.875rem;
  padding: 0 0.5rem;
  opacity: 0.75;
}
</style>
